import React from "react";
import { useClient } from "urql";
import { SearchContext } from "../../common/context/search";
import {
  useAccountSubscriptionsQuery,
  AccountSubscriptionsDocument,
} from "../../graphql/generated";
import { classNames } from "../../common/utils/classnames";
import { Transition } from "@headlessui/react";
import Loading from "../../common/components/loading";
import Error from "../../common/components/error";
import { useTitle } from "../../common/utils/title";
import AccountSubscriptionCard from "../components/card";
import CommonSwitch from "../../common/components/switch";

const AccountSubscriptionList: React.FC = () => {
  useTitle("Account Subscription");
  const [loading, setLoading] = React.useState(false);
  const graphqlClient = useClient();
  const [active, setActive] = React.useState(true);
  const [hasManualOverride, setHasManualOverride] = React.useState(false);
  const { searchBounced } = React.useContext(SearchContext);

  const [{ data, error, fetching }] = useAccountSubscriptionsQuery({
    variables: {
      first: 20,
      active: active ? active : undefined,
      accountId: searchBounced,
      hasManualOverride: hasManualOverride,
    },
  });

  const fetchMore = React.useCallback(async () => {
    if (data?.accountSubscriptions.pageInfo.hasNextPage) {
      console.log(
        "I have a next page with an end cursor " +
          data.accountSubscriptions.pageInfo.endCursor
      );
      setLoading(true);
      await graphqlClient
        .query(AccountSubscriptionsDocument, {
          first: 20,
          after: data.accountSubscriptions.pageInfo.endCursor,
          active: active ? active : undefined,
          accountId: searchBounced,
          hasManualOverride: hasManualOverride,
        })
        .toPromise();
      setLoading(false);
    }
  }, [data, active, graphqlClient, searchBounced, hasManualOverride]);

  return (
    <>
      <Loading show={!data && !error} />
      <Error error={error} />
      <Transition
        show={!!data}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="sm:flex sm:items-center sm:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Account Subscriptions
                <span className="text-sm text-gray-500 px-2">
                  {fetching
                    ? "Loading..."
                    : `${data?.accountSubscriptions.total.toLocaleString()}`}
                </span>
              </h2>
              <div className="mt-1 grid grid-cols-1 gap-0">
                <div className="mt-2 flex items-center text-sm text-gray-500">
                  <CommonSwitch
                    enabled={active}
                    toggle={() => setActive(!active)}
                    label="Active Only"
                  />
                  <CommonSwitch
                    enabled={hasManualOverride}
                    toggle={() => setHasManualOverride(!hasManualOverride)}
                    label="Manual Override?"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="p-4">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {data?.accountSubscriptions?.edges?.map((subscription) => (
                  <AccountSubscriptionCard
                    subscription={subscription?.node}
                    key={subscription?.node.id}
                  />
                ))}
              </div>
            </div>
          </div>
          {data &&
            data?.accountSubscriptions.total >
              (data?.accountSubscriptions?.edges?.length ?? 0) && (
              <div className="min-w-full divide-y divide-gray-200">
                <div className="p-4">
                  <button
                    className={classNames(
                      loading
                        ? "bg-gray-500 text-white"
                        : "bg-zenlist-500 hover:bg-zenlist-700 text-white",
                      "font-bold py-2 px-4 rounded"
                    )}
                    onClick={() => fetchMore()}
                    disabled={loading}
                  >
                    Fetch More
                  </button>
                </div>
              </div>
            )}
        </div>
      </Transition>
    </>
  );
};

export default AccountSubscriptionList;
